import { io, Socket } from 'socket.io-client';
import { reactive } from 'vue';
import baseAPI from '@/api/base';
import { chatAppSide } from '@/utils/chat';

/**
 * The support chat's live connection: the API's socket server (the one the
 * screens use) tells a subscribed app the moment a conversation changes — a
 * message sent, changed, deleted or reacted to, the other side reading, the
 * conversation started, resolved, reopened or renamed — and passes on the
 * other side typing. A notice only says what changed and in which
 * conversation; the chat fetches the conversation itself (utils/chat).
 *
 * One connection however many parts of the app want it: each asks with
 * useChatRealtime() and lets go with what it returns. While the connection is
 * up and subscribed, `chatRealtime.live` is true and the chat polls rarely;
 * without it — a proxy that blocks it, a dropped connection — everything still
 * works, by polling.
 *
 * The same module lives in the admin and both apps (see utils/chat).
 */

export const CHAT_SOCKET_EVENT = 'SUPPORT_CHAT_EVENT';

export interface ChatEvent {
  // 'conversation': started, resolved, reopened, renamed, rated or (staff
  // only) assigned (`changes`).
  type: 'message' | 'message-updated' | 'read' | 'typing' | 'conversation';
  organizationId: string;
  conversationId?: string;
  messageId?: string;
  side?: 'staff' | 'organization';
  userId?: string;
  name?: string;
  readAt?: string;
  // A message that started its conversation.
  started?: boolean;
  changes?: Array<'resolved' | 'reopened' | 'renamed' | 'rated' | 'assigned'>;
  // An assignment: who it is now assigned to (null: nobody), and who did it.
  assignedToId?: string | null;
  assignedById?: string;
}

export const chatRealtime = reactive({ live: false });

type Handler = (event: ChatEvent) => void;

const handlers = new Set<Handler>();
let socket: Socket | null = null;
let tokenOf: (() => string | null) | null = null;
let users = 0;

/** Where the API is: the socket server sits at its root. */
function apiOrigin(): string {
  try {
    return new URL(String(baseAPI.defaults.baseURL || ''), window.location.href).origin;
  } catch (error) {
    return window.location.origin;
  }
}

function subscribe(): void {
  const token = tokenOf ? tokenOf() : null;
  if (!socket || !socket.connected || !token) {
    chatRealtime.live = false;
    return;
  }
  // The side too: in an organization's app a staff account is the organization.
  socket.timeout(10000).emit('chat:subscribe', { token, as: chatAppSide() }, (error: unknown, answer: any) => {
    chatRealtime.live = !error && Boolean(answer && answer.ok);
  });
}

/**
 * Starts the connection, or joins the one already up. `getToken` gives the
 * signed-in session's token. Returns the function that lets go of it; the
 * connection closes once nobody holds it.
 */
export function useChatRealtime(getToken: () => string | null): () => void {
  users += 1;
  tokenOf = getToken;

  if (!socket) {
    socket = io(apiOrigin(), {
      transports: ['websocket', 'polling'],
      withCredentials: true,
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
    });
    socket.on('connect', subscribe);
    socket.on('disconnect', () => {
      chatRealtime.live = false;
    });
    socket.on(CHAT_SOCKET_EVENT, (event: ChatEvent) => {
      if (!event || typeof event !== 'object') return;
      handlers.forEach((handler) => {
        try {
          handler(event);
        } catch (error) {
          console.error('Support chat: a live notice could not be handled.', error);
        }
      });
    });
  } else {
    subscribe();
  }

  let released = false;
  return () => {
    if (released) return;
    released = true;
    users = Math.max(users - 1, 0);
    if (users > 0 || !socket) return;
    socket.emit('chat:unsubscribe');
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
    tokenOf = null;
    chatRealtime.live = false;
  };
}

/** Listens for chat notices; returns the function that stops listening. */
export function onChatEvent(handler: Handler): () => void {
  handlers.add(handler);
  return () => {
    handlers.delete(handler);
  };
}

/** Tells the other side someone is typing in a conversation — staff also name its organization. */
export function sendChatTyping(where: { conversationId: string; organizationId?: string | null }): void {
  if (!socket || !chatRealtime.live || !where.conversationId) return;
  socket.emit('chat:typing', where.organizationId
    ? { conversationId: where.conversationId, organizationId: where.organizationId }
    : { conversationId: where.conversationId });
}
